import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import ErpBookingReportCard from './ErpBookingReportCard';
import Fold from './Fold';

const { Text } = Typography;

const STATUS_COLOR = {
  ok: 'green',
  waiting: 'blue',
  missing: 'red',
  failed: 'red',
  never: 'orange',
  inactive: 'default',
};

const ATTEMPT_COLOR = { ok: 'green', failed: 'red', rejected: 'volcano' };

const when = (value) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—');

/**
 * Every night's pull from the AB machine, company by company: what arrived,
 * what failed, and what never came.
 *
 * The deliveries tab shows one company's successful deliveries; this shows all
 * companies at once and, unlike that tab, the failures too — a run that fell
 * over used to leave its only trace in a log file on the Windows machine.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.withSettings] - Also show who gets warned and when.
 * @returns {JSX.Element} The panel
 */
export default function AbPullsPanel({ withSettings = false }) {
  const { t } = useTranslation('clientReports');
  const tp = (key, options) => t(`pulls.${key}`, options);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['client-reports-ab-pulls'],
    queryFn: () => API.clientReports.getAbPulls({ limit: 300 }),
    refetchOnWindowFocus: false,
  });

  const pulls = data?.data || {};
  const companies = pulls.companies || [];
  const attempts = pulls.attempts || [];

  const [form, setForm] = useState(null);
  useEffect(() => {
    if (pulls.settings && !form) setForm(pulls.settings);
  }, [pulls.settings, form]);

  const save = useMutation({
    mutationFn: (settings) => API.clientReports.saveAbAlerts(settings),
    onSuccess: () => {
      message.success(tp('saved'));
      queryClient.invalidateQueries({ queryKey: ['client-reports-ab-pulls'] });
    },
    onError: (e) =>
      message.error(e?.response?.data?.message || e?.message || tp('saveError')),
  });

  const test = useMutation({
    mutationFn: () => API.clientReports.testAbAlert(),
    onSuccess: (res) => {
      const sent = res?.data?.sent;
      message[sent ? 'success' : 'warning'](
        sent
          ? tp('testSent', { list: (res.data.recipients || []).join(', ') })
          : tp('testNotSent')
      );
    },
    onError: (e) =>
      message.error(e?.response?.data?.message || e?.message || tp('testError')),
  });

  const problems = companies.filter(
    (c) => c.status === 'missing' || c.status === 'failed' || c.status === 'never'
  );

  const companyColumns = [
    {
      title: tp('company'),
      dataIndex: 'companyName',
      key: 'companyName',
      width: 240,
    },
    {
      title: tp('status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLOR[status] || 'default'}>
          {tp(`status_${status}`)}
        </Tag>
      ),
    },
    {
      title: tp('lastSuccess'),
      dataIndex: 'lastSuccessAt',
      key: 'lastSuccessAt',
      width: 160,
      render: (value, row) => (
        <Text type={row.stale ? 'danger' : undefined}>{when(value)}</Text>
      ),
    },
    {
      title: tp('lines'),
      dataIndex: 'lastSuccessLines',
      key: 'lastSuccessLines',
      width: 90,
      align: 'right',
      render: (value) => (value ? value.toLocaleString('sr-RS') : '—'),
    },
    {
      title: tp('lastEntry'),
      dataIndex: 'lastEntryDate',
      key: 'lastEntryDate',
      width: 120,
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '—'),
    },
    {
      title: tp('source'),
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (value) => value || '—',
    },
    {
      title: tp('machine'),
      dataIndex: 'machine',
      key: 'machine',
      width: 150,
      render: (value) => value || '—',
    },
    {
      title: tp('lastError'),
      dataIndex: 'lastError',
      key: 'lastError',
      render: (error) =>
        error ? (
          <Tooltip title={`${when(error.at)} · ${error.phase}`}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {error.message || tp('noMessage')}
            </Text>
          </Tooltip>
        ) : (
          '—'
        ),
    },
  ];

  const attemptColumns = [
    {
      title: tp('at'),
      dataIndex: 'at',
      key: 'at',
      width: 150,
      render: when,
    },
    {
      title: tp('company'),
      dataIndex: 'companyName',
      key: 'companyName',
      width: 220,
      render: (value, row) => value || row.clientId || '—',
    },
    {
      title: tp('phase'),
      dataIndex: 'phase',
      key: 'phase',
      width: 110,
      render: (phase) => tp(`phase_${phase}`),
    },
    {
      title: tp('outcome'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={ATTEMPT_COLOR[status] || 'default'}>
          {tp(`outcome_${status}`)}
        </Tag>
      ),
    },
    {
      title: tp('lines'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 90,
      align: 'right',
      render: (value) => (value ? value.toLocaleString('sr-RS') : '—'),
    },
    {
      title: tp('duration'),
      dataIndex: 'durationMs',
      key: 'durationMs',
      width: 90,
      align: 'right',
      render: (value) => (value ? `${Math.round(value / 100) / 10} s` : '—'),
    },
    {
      title: tp('message'),
      dataIndex: 'message',
      key: 'message',
      render: (value, row) => (
        <Text type={row.status === 'ok' ? undefined : 'danger'} style={{ fontSize: 12 }}>
          {value || '—'}
        </Text>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Button
          shape="round"
          icon={<ReloadOutlined />}
          loading={isFetching}
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ['client-reports-ab-pulls'],
            })
          }
        >
          {tp('refresh')}
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {tp('nightStart', { at: when(pulls.nightStart) })}
        </Text>
      </div>

      {!isLoading && problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={tp('problemsTitle', { count: problems.length })}
          description={tp('problemsHint')}
        />
      )}

      <Fold title={tp('overviewTitle')} defaultOpen>
        <Table
          rowKey="companyId"
          size="small"
          loading={isLoading}
          columns={companyColumns}
          dataSource={companies}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Fold>

      {withSettings && form && (
        <Fold title={tp('alertsTitle')}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {tp('alertsHint')}
            </Text>

            <Space size={12} wrap>
              <Switch
                checked={form.enabled}
                onChange={(enabled) => setForm({ ...form, enabled })}
              />
              <Text>{tp('alertsEnabled')}</Text>
            </Space>

            <div style={{ maxWidth: 520 }}>
              <Text style={{ fontSize: 12 }}>{tp('recipients')}</Text>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                tokenSeparators={[',', ' ', ';']}
                value={form.recipients}
                onChange={(recipients) => setForm({ ...form, recipients })}
                placeholder="nikola@hlb.rs"
              />
            </div>

            <Space size={16} wrap>
              <Space size={8}>
                <Text style={{ fontSize: 12 }}>{tp('checkHour')}</Text>
                <InputNumber
                  min={0}
                  max={23}
                  value={form.checkHour}
                  onChange={(checkHour) => setForm({ ...form, checkHour })}
                />
              </Space>
              <Space size={8}>
                <Text style={{ fontSize: 12 }}>{tp('staleHours')}</Text>
                <InputNumber
                  min={1}
                  max={336}
                  value={form.staleHours}
                  onChange={(staleHours) => setForm({ ...form, staleHours })}
                />
              </Space>
            </Space>

            <Space size={8} wrap>
              <Button
                className="btn-primary"
                loading={save.isPending}
                onClick={() => save.mutate(form)}
              >
                {tp('save')}
              </Button>
              <Button
                shape="round"
                icon={<SendOutlined />}
                loading={test.isPending}
                onClick={() => test.mutate()}
              >
                {tp('sendTest')}
              </Button>
            </Space>

            <Text type="secondary" style={{ fontSize: 12 }}>
              {tp('lastChecked', {
                checked: when(pulls.settings?.lastCheckedAt),
                alerted: when(pulls.settings?.lastAlertAt),
              })}
            </Text>
          </Space>
        </Fold>
      )}

      {withSettings && <ErpBookingReportCard />}

      <Fold title={tp('historyTitle')}>
        <Table
          rowKey="_id"
          size="small"
          loading={isLoading}
          columns={attemptColumns}
          dataSource={attempts}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
        />
      </Fold>
    </Space>
  );
}
